import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Download,
  Delete,
  Close,
  LocalHospital,
  Upload
} from '@mui/icons-material';
import SecuredLayout from '../components/layouts/SecuredLayout';

const Prescriptions = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data - replace with API calls
  useEffect(() => {
    setPrescriptions([
      {
        id: 1,
        name: "Prescription 1",
        date: "2024-01-15",
        doctor: "Dr. Smith",
        file_url: "/prescriptions/prescription1.pdf"
      },
      {
        id: 2,
        name: "Prescription 2", 
        date: "2024-01-10",
        doctor: "Dr. Johnson",
        file_url: "/prescriptions/prescription2.pdf"
      }
    ]);
  }, []);

  const handleDownloadPrescription = async (id) => {
    setIsLoading(true);
    try {
      // Simulate API call for downloading prescription
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In a real app, you would trigger the actual download
      console.log(`Downloading prescription ${id}`);
      
      // Create a mock download link
      const prescription = prescriptions.find(p => p.id === id);
      if (prescription) {
        // This is a mock implementation - in real app you'd get the actual file
        const link = document.createElement('a');
        link.href = prescription.file_url;
        link.download = `${prescription.name}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading prescription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  const handleDeletePrescription = async () => {
    setIsLoading(true);
    try {
      // Simulate API call for deleting prescription
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setPrescriptions(prev => prev.filter(p => p.id !== deleteId));
      setDeleteDialog(false);
      
      console.log('Prescription deleted successfully!');
    } catch (error) {
      console.error('Error deleting prescription:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadPrescription = () => {
    // Simulate file upload
    console.log('Uploading new prescription...');
    // In a real app, you would implement file upload logic here
  };

  return (
    <SecuredLayout 
      activeTab={3} 
      pageTitle="Prescriptions"
      contentCols={10}
    >
        {/* Upload Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocalHospital color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Upload New Prescription</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your prescription files for easy access and management.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleUploadPrescription}
            >
              Upload Prescription
            </Button>
          </CardContent>
        </Card>

        {/* Prescriptions List */}
        <Grid container spacing={2}>
          {prescriptions.map((prescription) => (
            <Grid item xs={12} md={6} key={prescription.id}>
              <Card elevation={1}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocalHospital color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" component="div">
                      {prescription.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Doctor: {prescription.doctor}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary">
                    Date: {new Date(prescription.date).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Tooltip title="Download Prescription">
                    <IconButton
                      color="primary"
                      onClick={() => handleDownloadPrescription(prescription.id)}
                      disabled={isLoading}
                    >
                      <Download />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete Prescription">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteConfirm(prescription.id)}
                      disabled={isLoading}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {prescriptions.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <LocalHospital sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Prescriptions Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your first prescription to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Upload />}
              onClick={handleUploadPrescription}
            >
              Upload Prescription
            </Button>
          </Paper>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm">
          <DialogTitle>
            Are you sure you want to delete this prescription?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. The prescription file will be permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleDeletePrescription} 
              color="error" 
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Overlay */}
        {isLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
    </SecuredLayout>
  );
};

export default Prescriptions;
